import React, { useState, useEffect } from "react";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import OperationsPageHeader from "../home/top_bar_components/OperationsPageHeader";
import SubTabs from "../home/top_bar_components/SubTabs";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { getDefaultAxiosConfig } from "../../../utilities/requests";
import {
  getRightworkAPI,
  postRightworkAPI,
} from "../requests/utilities/requests";
import {
  API_get_role_names,
  API_get_plan_tasks,
  API_get_plan_task_templates,
  API_get_now_tasks,
  API_get_plan_task_overview,
  API_set_plan_task_status,
  API_post_plan_task,
  API_get_store_groups,
  API_import_plan_task_templates,
  API_delete_plan_task,
} from "../../../constants";

import NowTable from "./tasks_hub/NowTable";
import OverviewSegRow from "./tasks_hub/OverviewSegRow";
import OverviewGrid from "./tasks_hub/OverviewGrid";
import OverviewOneOffTable from "./tasks_hub/OverviewOneOffTable";
import TaskLibrary from "./tasks_hub/TaskLibrary";
import TaskDrawerHost from "./tasks_hub/TaskDrawerHost";
import ConfirmDialog from "../boards/ConfirmDialog";
import TaskTemplateImportModal from "./plan_contents/TaskTemplateImportModal";
import DailyPlanFormBuilder from "./plan_contents/DailyPlanFormBuilder";
import TaskReports from "./tasks_hub/TaskReports";
import TaskReportDetail from "./tasks_hub/TaskReportDetail";
import {
  formatDateYMD,
  cadenceLabel,
  formatDateTimeShort,
  windowLabel,
  scopeLabel,
  audienceLabel,
  getBusinessWeekStartDayIndex,
  periodWindow,
  stepWindow,
  resetLabelFor,
  windowLabelFor,
} from "./tasks_hub/taskHubUtils";

// The Tasks hub. Tasks surface to everyone on the store roster whose role
// matches (or everyone, when the task has no roles), so this area is built
// around getting work done and seeing how routines are going:
//   Now       - everything that can be done right now, one list sorted by due
//   Overview  - completion grid per cadence (one-off / daily / weekly / monthly)
//   Library   - task definitions and their lifecycle (draft / active / paused)
//   Reports   - per-task completion analytics

const FORM_DEFAULT_DATA = {
  name: "",
  description: "",
  subtasks: [],
  uuid: null,
  category: "",
  type: "repeating",
  cadence: "daily",
  day_of_month: null,
  due_at: null,
  one_off_date: null,
  requires_validation: false,
  track_time: false,
  scoring_enabled: false,
  grade_bands: null,
  audience_permission_types: [],
  lower_bound_qhnum: null,
  upper_bound_qhnum: null,
  days_of_week: [0, 1, 2, 3, 4, 5, 6],
  file_uploads: [],
  is_auto_assignable: true,
  auto_assignable_roles: [],
  is_location_specific: false,
  location_specific_stores: [],
  location_specific_store_groups: [],
};

const TABS = [
  { key: "now", label: "Now" },
  { key: "overview", label: "Overview" },
  { key: "library", label: "Library" },
  { key: "reports", label: "Reports" },
];

// What a tab shows when its request failed. A spinner would be a lie, and an
// empty state would read as "nothing to do today".
const TabLoadError = ({ onRetry }) => (
  <div className="flex flex-col items-center justify-center py-24 gap-3">
    <p className="text-sm text-slate-500">These tasks could not be loaded.</p>
    <button
      onClick={onRetry}
      className="text-sm font-semibold text-violet-700 hover:text-violet-800"
    >
      Try again
    </button>
  </div>
);

function DailyPlanDisplay(props) {
  const [activeTab, setActiveTab] = useState("now");
  // Page spinner only for the first load and a store switch. Tab, sub-tab,
  // toggle, and stepper changes refetch in place (tabLoading) so the shell
  // never unmounts and nothing jumps.
  const [isLoading, setIsLoading] = useState(true);
  const [tabLoading, setTabLoading] = useState(false);

  const weekStartIndex = getBusinessWeekStartDayIndex(props.storeNames, props.homeStore);

  // Now
  const [nowData, setNowData] = useState(null);
  // Request failure is its own state on both tabs: without it, cleared data
  // reads as "still loading" and the tab spins with nothing to act on.
  const [nowError, setNowError] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const [showUpcoming, setShowUpcoming] = useState(false);

  // Overview
  const [overviewCadence, setOverviewCadence] = useState("daily");
  const [overviewWindowStart, setOverviewWindowStart] = useState(
    periodWindow("daily", new Date(), weekStartIndex).start
  );
  const [overviewData, setOverviewData] = useState(null);
  const [overviewError, setOverviewError] = useState(false);
  const [libraryError, setLibraryError] = useState(false);

  // Library (also names each task's eligible roles for the drawer/assign picker)
  const [planTasks, setPlanTasks] = useState([]);
  const [roleNames, setRoleNames] = useState([]);
  const [taskTemplates, setTaskTemplates] = useState([]);
  const [templateGroups, setTemplateGroups] = useState([]);
  const [importModalOpen, setImportModalOpen] = useState(false);

  // The detail drawer (and its assign / validate / checklist overlays) is
  // owned by TaskDrawerHost; this ref opens and refreshes it.
  const drawerHost = React.useRef(null);
  const [confirmActivate, setConfirmActivate] = useState(null);
  // {task, scope} while drilled into one task's report
  const [reportDrill, setReportDrill] = useState(null);

  // Library form (reuses the existing form builder)
  const [viewMode, setViewMode] = useState("hub"); // "hub" | "form"
  const [modalData, setModalData] = useState(FORM_DEFAULT_DATA);
  const [modalEdit, setModalEdit] = useState(false);

  // The store's business date, from whichever payload has loaded; the device
  // clock only fills in before the first response (it can be a day off around
  // midnight or from another timezone).
  const todayStr = nowData?.date || overviewData?.today || formatDateYMD(new Date());
  const todayDate = new Date(`${todayStr}T12:00:00`);

  // The Overview window opens on the device date and re-anchors to the store
  // business date once a payload supplies it, unless the manager has already
  // stepped away from the current window.
  const steppedAwayRef = React.useRef(false);
  useEffect(() => {
    if (steppedAwayRef.current) return;
    const anchored = periodWindow(overviewCadence, todayDate, weekStartIndex).start;
    if (formatDateYMD(anchored) !== formatDateYMD(overviewWindowStart)) {
      setOverviewWindowStart(anchored);
    }
    // The store (and so its business week start) is part of the anchor: a
    // store switch on Weekly must snap to the new store's week boundary even
    // when the business date is unchanged.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [todayStr, overviewCadence, weekStartIndex, props.homeStore]);

  // ---------- fetches ----------

  // Context refs: a response only lands if it is still for what is being
  // viewed, so a slow request from the previous store or window never
  // overwrites the current one.
  const nowContextRef = React.useRef(null);
  const overviewContextRef = React.useRef(null);

  const fetchNowTasks = async () => {
    const context = `${props.homeStore}:${showDone}:${showUpcoming}`;
    nowContextRef.current = context;
    try {
      const res = await getRightworkAPI(API_get_now_tasks, {
        store_id: props.homeStore,
        include_done: showDone ? "true" : "false",
        include_upcoming: showUpcoming ? "true" : "false",
      });
      if (nowContextRef.current !== context) return;
      // A failed fetch clears the rows rather than leaving the previous store's
      // tasks on screen under the newly selected one.
      setNowData(res.status === 200 ? res.data?.redux?.payload || null : null);
      setNowError(res.status !== 200);
    } catch (error) {
      // The helper throws on any HTTP or network failure, and an unhandled
      // rejection would leave the tab spinning with nothing to act on.
      console.error("Error fetching now tasks:", error);
      if (nowContextRef.current !== context) return;
      setNowData(null);
      setNowError(true);
    }
  };

  const fetchOverview = async () => {
    const context = `${props.homeStore}:${overviewCadence}:${formatDateYMD(overviewWindowStart)}`;
    overviewContextRef.current = context;
    try {
      const res = await getRightworkAPI(API_get_plan_task_overview, {
        store_id: props.homeStore,
        cadence: overviewCadence,
        window_start: formatDateYMD(overviewWindowStart),
      });
      if (overviewContextRef.current !== context) return;
      setOverviewData(res.status === 200 ? res.data?.redux?.payload || null : null);
      setOverviewError(res.status !== 200);
    } catch (error) {
      console.error("Error fetching task overview:", error);
      if (overviewContextRef.current !== context) return;
      setOverviewData(null);
      setOverviewError(true);
    }
  };

  useEffect(() => {
    // Everything store-scoped goes at once: the spinner shows until the new
    // store's payloads land, and no row from the previous store can be opened
    // or saved against the new store id in between.
    setNowData(null);
    setNowError(false);
    setOverviewData(null);
    setOverviewError(false);
  }, [props.homeStore]);

  const fetchPlanTasks = async () => {
    try {
      const res = await getRightworkAPI(API_get_plan_tasks, {
        company_id: props.homeCompany,
      });
      // A failure must not leave the previous company's tasks in the Library,
      // nor let an empty list read as "this company has no tasks yet".
      setPlanTasks(res.status === 200 ? res.data?.redux?.payload?.plan_tasks || [] : []);
      setLibraryError(res.status !== 200);
    } catch (error) {
      console.error("Error fetching task library:", error);
      setPlanTasks([]);
      setLibraryError(true);
    }
  };

  // These three carry their own catch so one failure cannot reject the batch
  // they share with the tab's own fetch, which would leave that tab with
  // neither rows nor an error to show.
  const fetchRoleNames = async () => {
    try {
      const axiosConfig = await getDefaultAxiosConfig();
      const response = await axios.get(
        `${API_get_role_names}${props.homeStore}`,
        axiosConfig
      );
      setRoleNames(response.data || []);
    } catch (error) {
      console.error("Error fetching role names:", error);
    }
  };

  const fetchPlanTaskTemplates = async () => {
    try {
      const res = await getRightworkAPI(API_get_plan_task_templates, {
        company_id: props.homeCompany,
      });
      if (res.status === 200) {
        const payload = res.data?.redux?.payload;
        setTaskTemplates(payload?.task_templates || []);
        setTemplateGroups(payload?.template_groups || []);
      }
    } catch (error) {
      console.error("Error fetching task templates:", error);
    }
  };

  // Regions and districts for the form's location picker and the Library's
  // scope label. Optional: a failure leaves the flat store list in place.
  const [storeGroups, setStoreGroups] = useState(null);
  const storeGroupsContextRef = React.useRef(null);
  const fetchStoreGroups = async () => {
    // Same guard as the Now and Overview fetches: a response for a company the
    // user has since switched away from is dropped, not stored.
    const company = props.homeCompany;
    const context = `${company}`;
    storeGroupsContextRef.current = context;
    let next = null;
    try {
      const res = await getRightworkAPI(API_get_store_groups, { company_id: company });
      const payload = res.data?.redux?.payload;
      next = payload && Number(payload.company_id) === Number(company) ? payload : null;
    } catch (error) {
      next = null;
    }
    if (storeGroupsContextRef.current !== context) return;
    setStoreGroups(next);
  };

  const fetchData = async (showSpinner = true) => {
    if (showSpinner) setIsLoading(true);
    else setTabLoading(true);
    try {
      const jobs = [fetchPlanTasks(), fetchRoleNames()];
      if (showSpinner) jobs.push(fetchStoreGroups());
      if (activeTab === "now") {
        jobs.push(fetchNowTasks());
      } else if (activeTab === "overview") {
        jobs.push(fetchOverview());
      } else if (activeTab === "library") {
        jobs.push(fetchPlanTaskTemplates());
        // The store's timezone and business date ride on the Now payload
        // (the Library payload has neither): the activate dialog's deadline
        // and the form builder's due-time picker read the store's clock, so
        // load it on a store change (showSpinner; the cleared payloads are
        // not visible to this render yet) and whenever it is missing.
        if (showSpinner || (!nowData && !overviewData)) jobs.push(fetchNowTasks());

      }

      await Promise.all(jobs);
    } catch (error) {
      console.error("Error fetching tasks data:", error);
    } finally {
      setIsLoading(false);
      setTabLoading(false);
    }
  };

  const baseKeyRef = React.useRef(null);
  useEffect(() => {
    const baseKey = `${props.homeStore}:${props.homeCompany}`;
    const baseChanged = baseKeyRef.current !== baseKey;
    baseKeyRef.current = baseKey;
    fetchData(baseChanged);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.homeStore, props.homeCompany, activeTab, showDone, showUpcoming, overviewCadence, overviewWindowStart]);

  // Re-fetch when an assistant action confirms a task mutation.
  const lastOpsSignalTimestampRef = React.useRef(null);
  useEffect(() => {
    const ts = props.opsRefreshSignal?.timestamp;
    if (!ts || ts === lastOpsSignalTimestampRef.current) return;
    lastOpsSignalTimestampRef.current = ts;
    if (!props.opsRefreshSignal?.types?.includes("tasks")) return;
    fetchData(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.opsRefreshSignal]);

  // ---------- derived ----------

  // The day payload does not include each task's eligible roles; the library
  // payload does. Join them so the drawer and assign picker can name roles.
  // The Overview payload carries a task summary without the window fields, so
  // the grid's subline reads them off the library payload instead.
  const taskByUuid = React.useMemo(() => {
    const map = {};
    (planTasks || []).forEach((t) => {
      map[t.uuid] = t;
    });
    return map;
  }, [planTasks]);

  const canManage = ["company_admin", "company_manager", "rightwork_internal"].includes(
    props.permissionRole
  ) || !props.permissionRole;
  // Mirrors the backend rule: post/status/delete reject non-admin edits of
  // company-wide tasks, and of location-specific tasks unless EVERY one of
  // the task's stores is within the manager's managed stores.
  const companyStoreIds = React.useMemo(
    () =>
      new Set(
        (props.storeNames || [])
          .filter((s) => s.company === props.homeCompany)
          .map((s) => s.id)
      ),
    [props.storeNames, props.homeCompany]
  );
  const isAdminUser =
    !props.permissionRole ||
    (props.storeOptions || []).some(
      (m) =>
        ["company_admin", "rightwork_internal"].includes(m.permission_type) &&
        companyStoreIds.has(m.store)
    );
  const managedStoreIds = React.useMemo(
    () =>
      new Set(
        (props.storeOptions || [])
          .filter((m) =>
            ["company_manager", "company_admin", "rightwork_internal"].includes(
              m.permission_type
            )
          )
          .map((m) => m.store)
      ),
    [props.storeOptions]
  );
  const canMutateTask = (t) => {
    if (isAdminUser) return true;
    if (!t?.is_location_specific) return false;
    const ids = t.location_specific_stores || [];
    return ids.length > 0 && ids.every((id) => managedStoreIds.has(id));
  };

  // ---------- actions ----------

  const showError = (error, fallback = "Request failed") => {
    toast.dismiss();
    toast.error(error?.response?.data?.system?.message || fallback, { autoClose: 4000 });
  };

  // What the drawer host calls after an action changed an occurrence (it has
  // refetched its own day payload by then): the list behind it.
  const refreshLists = async () => {
    const jobs = [];
    if (activeTab === "now") jobs.push(fetchNowTasks());
    if (activeTab === "overview") jobs.push(fetchOverview());
    await Promise.all(jobs);
  };

  // tab: which drawer tab to land on (details | conversation | timeline).
  const openDrawerFor = (uuid, date, slotIndex = null, tab = "details") =>
    drawerHost.current?.open(uuid, date, slotIndex, tab);

  const setTaskStatus = async (task, status, successMessage) => {
    try {
      toast.loading("Saving...", { autoClose: false });
      const res = await postRightworkAPI(API_set_plan_task_status, {
        company_id: props.homeCompany,
        plan_task_uuid: task.uuid,
        status,
      });
      toast.dismiss();
      if (res.status === 200) {
        toast.success(successMessage, { autoClose: 3000 });
        setConfirmActivate(null);
        fetchData(false);
      }
    } catch (error) {
      showError(error);
    }
  };

  const post_plan_task = async (task) => {
    const requestData = {
      plan_task_uuid: task.plan_task_uuid || null,
      company_id: props.homeCompany,
      name: task.name,
      description: task.description,
      category: task.category,
      cadence: task.cadence,
      lower_bound_qhnum: task.lower_bound_qhnum,
      upper_bound_qhnum: task.upper_bound_qhnum,
      requires_validation: !!task.requires_validation,
      track_time: !!task.track_time,
      scoring_enabled: !!task.scoring_enabled,
      // [] = the defaults; the backend validates and sorts them.
      grade_bands: JSON.stringify(task.scoring_enabled ? task.grade_bands || [] : []),
      audience_permission_types: JSON.stringify(task.audience_permission_types || []),
      subtasks: JSON.stringify(task.subtasks || []),

      file_upload_uuids: JSON.stringify(task.file_uploads || []),
      is_auto_assignable: true,
      roles: JSON.stringify(task.auto_assignable_roles || []),
      auto_assignable_roles: JSON.stringify(task.auto_assignable_roles || []),
      is_location_specific: task.is_location_specific,
      // Stores picked on their own; picked regions and districts expand on the backend.
      location_specific_stores: JSON.stringify(task.location_specific_stores || []),
      location_specific_store_groups: JSON.stringify(task.location_specific_store_groups || []),
      intraday_repeat_interval_minutes:
        task.cadence === "daily" ? task.intraday_repeat_interval_minutes ?? null : null,
    };
    if (task.cadence === "daily" || task.cadence === "weekly") {
      requestData.days_of_week = JSON.stringify(task.days_of_week || []);
    }
    if (task.cadence === "monthly") {
      requestData.day_of_month = task.day_of_month;
    }
    if (task.cadence === "one_off") {
      requestData.due_at = task.due_at;
      if (task.one_off_date) requestData.one_off_date = task.one_off_date;
    }
    try {
      toast.loading("Saving task...", { autoClose: false });
      const res = await postRightworkAPI(API_post_plan_task, requestData);
      toast.dismiss();
      if (res.status === 200) {
        toast.success("Task saved", { autoClose: 3000 });
        fetchData(false);
      }
    } catch (error) {
      showError(error);
    }
  };

  const import_plan_task_templates = async (
    template_keys,
    is_location_specific,
    location_specific_stores
  ) => {
    try {
      toast.loading("Importing templates...", { autoClose: false });
      const res = await postRightworkAPI(API_import_plan_task_templates, {
        company_id: props.homeCompany,
        template_keys: JSON.stringify(template_keys),
        is_location_specific,
        location_specific_stores: JSON.stringify(location_specific_stores),
      });
      toast.dismiss();
      if (res.status === 200) {
        const payload = res.data?.redux?.payload;
        const createdCount = payload?.created_tasks?.length || 0;
        const skippedCount = payload?.skipped_templates?.length || 0;
        if (createdCount > 0) {
          toast.success(
            `${createdCount} task template${createdCount !== 1 ? "s" : ""} imported`,
            { autoClose: 3000 }
          );
        } else if (skippedCount > 0) {
          toast.info("Selected templates were already imported", { autoClose: 3000 });
        } else {
          toast.info("No templates imported", { autoClose: 3000 });
        }
        await Promise.all([fetchPlanTasks(), fetchPlanTaskTemplates()]);
      }
    } catch (error) {
      showError(error);
    }
  };

  const delete_plan_task = async (plan_task_uuid) => {
    try {
      toast.loading("Deleting task...", { autoClose: false });
      const res = await postRightworkAPI(API_delete_plan_task, {
        plan_task_uuid,
        company_id: props.homeCompany,
      });
      toast.dismiss();
      if (res.status === 200) {
        toast.success("Task deleted", { autoClose: 3000 });
        fetchData(false);
      }
    } catch (error) {
      showError(error);
    }
  };

  const openNewTask = () => {
    setModalData(FORM_DEFAULT_DATA);
    setModalEdit(false);
    setViewMode("form");
  };

  const openEditTask = (task) => {
    // The form edits the picks: explicit stores plus groups. The row's
    // location_specific_stores is the expanded list (kept for the manager
    // check), so seed from the explicit rows when the backend sends them.
    const explicitStores = task.location_specific_explicit_stores ?? task.location_specific_stores ?? [];
    setModalData({
      ...task,
      location_specific_stores: explicitStores,
      location_specific_store_groups: task.location_specific_store_groups || [],
      // The stores the picked groups expand to today. A manager cannot pick
      // groups, so their "Make Copy" turns this into explicit stores.
      location_specific_derived_stores: (task.location_specific_stores || []).filter((id) => !explicitStores.includes(id)),
      location_specific_store_vias: task.location_specific_store_vias || {},
      location_specific_excluded_stores: task.location_specific_excluded_stores || [],
      location_specific_excluded_vias: task.location_specific_excluded_vias || {},
      // The schedule those exclusions were derived under; an edit to it
      // makes them stale (the backend re-derives on save), so the form drops
      // them the moment the deadline or start time changes.
      location_specific_excluded_for: {
        due_at: task.due_at ?? null,
        lower_bound_qhnum: task.lower_bound_qhnum ?? null,
        one_off_date: task.one_off_date ?? null,
      },
    });
    setModalEdit(true);
    setViewMode("form");
  };

  const distinctCategories = [
    ...new Set((planTasks || []).map((t) => t.category).filter(Boolean)),
  ];

  const changeCadence = (cadence) => {
    setOverviewCadence(cadence);
    steppedAwayRef.current = false;
    setOverviewWindowStart(periodWindow(cadence, todayDate, weekStartIndex).start);
  };
  const currentWindowStart = periodWindow(overviewCadence, todayDate, weekStartIndex).start;
  const isCurrentWindow = formatDateYMD(overviewWindowStart) === formatDateYMD(currentWindowStart);
  // Data for the cadence being shown; a response for the previous cadence has
  // the wrong shape (grid vs one-off list) and must not render into the new one.
  const overviewReady = !!overviewData && overviewData.cadence === overviewCadence;

  const overviewSummary = (() => {
    const s = overviewData?.summary;
    if (!s) return null;
    if (overviewCadence === "one_off") {
      return (
        <>
          {s.open || 0} open · <span className="font-semibold text-slate-900">{s.done || 0} done</span>
          {s.overdue ? ` · ${s.overdue} overdue` : ""}
        </>
      );
    }
    return (
      <>
        {s.task_count} routine{s.task_count === 1 ? "" : "s"}
        {s.on_time_pct != null && (
          <>
            {" · "}
            <span className="font-semibold text-slate-900">{s.on_time_pct}% on time</span>
          </>
        )}
        {s.missed ? ` · ${s.missed} missed` : ""}
      </>
    );
  })();

  // ---------- render ----------

  if (viewMode === "form") {
    return (
      <div className="my-5 mx-10">
        <DailyPlanFormBuilder
          modalData={modalData}
          setModalData={setModalData}
          modalEdit={modalEdit}
          handleCloseFormBuilder={() => {
            setViewMode("hub");
            fetchData(false);
          }}
          post_plan_task={post_plan_task}
          delete_plan_task={delete_plan_task}
          distinctCategories={distinctCategories}
          homeCompany={props.homeCompany}
          storeTz={nowData?.timezone || overviewData?.timezone}
          roleNames={roleNames}
          storeNames={props.storeNames}
          permissionRole={props.permissionRole}
          isAdminUser={isAdminUser}
          storeGroups={storeGroups}
          reloadStoreGroups={fetchStoreGroups}
          managedStoreIds={managedStoreIds}
        />
      </div>
    );
  }

  return (
    <div className="my-5">
      <OperationsPageHeader
        title="Tasks"
        scope="store"
        homeStore={props.homeStore}
        storeOptions={props.storeOptions}
        storeNames={props.storeNames}
        change_home_store={props.change_home_store}
      >
        <SubTabs
          className="mt-4"
          fullWidth
          tabs={TABS}
          activeTab={activeTab}
          onChange={(key) => {
            setActiveTab(key);
            setReportDrill(null);
          }}
          rightSlot={null}
        />
      </OperationsPageHeader>

      <div className="mt-5 mx-10 pb-20">
        {activeTab === "reports" ? (
          reportDrill ? (
            <TaskReportDetail
              task={reportDrill.task}
              scope={reportDrill.scope}
              homeCompany={props.homeCompany}
              homeStore={props.homeStore}
              onBack={() => setReportDrill(null)}
              onOpenOccurrence={(taskUuid, date) => drawerHost.current?.open(taskUuid, date)}
            />

          ) : (
            <TaskReports
              homeCompany={props.homeCompany}
              homeStore={props.homeStore}
              onPickTask={(task, scope) => setReportDrill({ task, scope })}
            />
          )
        ) : isLoading ? (
          <div className="flex justify-center py-24">
            <LoadSpinner />
          </div>
        ) : activeTab === "now" ? (
          nowError && !tabLoading ? (
            <TabLoadError onRetry={fetchNowTasks} />
          ) : nowData ? (
            // The rows stay on screen while a toggle or stepper refetches
            // (dimmed, not replaced by a spinner): flipping "Show done today"
            // should not blank the list.
            <div aria-busy={tabLoading} className={tabLoading ? "opacity-60 transition-opacity" : "transition-opacity"}>
              <NowTable
                data={nowData}
                storeId={props.homeStore}
                showDone={showDone}
                onToggleDone={setShowDone}
                showUpcoming={showUpcoming}
                onToggleUpcoming={setShowUpcoming}
                onOpenTask={(row, tab) => openDrawerFor(row.plan_task?.uuid, row.date, row.intraday_slot_index, tab)}
                onGoOverview={() => setActiveTab("overview")}
              />
            </div>
          ) : (
            <div className="flex justify-center py-24">
              <LoadSpinner />
            </div>
          )
        ) : activeTab === "overview" ? (
          <>
            <OverviewSegRow
              planTasks={planTasks}
              storeId={props.homeStore}
              cadence={overviewCadence}
              onChange={changeCadence}
              summary={overviewReady ? overviewSummary : null}
            />
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-1.5">
                <NavBtn
                  onClick={() => {
                    steppedAwayRef.current = true;
                    setOverviewWindowStart(stepWindow(overviewCadence, overviewWindowStart, -1));
                  }}
                  label="Earlier"
                >
                  <ChevronLeftIcon style={{ fontSize: "20px" }} />
                </NavBtn>
                <span className="min-w-[220px] text-center text-sm font-semibold text-slate-900 tabular-nums whitespace-nowrap">
                  {windowLabelFor(overviewCadence, overviewWindowStart)}
                </span>
                <NavBtn
                  onClick={() => {
                    steppedAwayRef.current = true;
                    setOverviewWindowStart(stepWindow(overviewCadence, overviewWindowStart, 1));
                  }}
                  label="Later"
                >
                  <ChevronRightIcon style={{ fontSize: "20px" }} />
                </NavBtn>
                {!isCurrentWindow && (
                  <button
                    onClick={() => {
                      steppedAwayRef.current = false;
                      setOverviewWindowStart(currentWindowStart);
                    }}
                    className="ml-1 text-xs font-semibold rounded-lg px-3 py-1.5 border text-violet-700 bg-white border-slate-200 hover:border-violet-300"
                  >
                    {resetLabelFor(overviewCadence)}
                  </button>
                )}
              </div>
            </div>
            {overviewError && !tabLoading ? (
              <TabLoadError onRetry={fetchOverview} />
            ) : !overviewReady || tabLoading ? (
              <div className="flex justify-center py-24">
                <LoadSpinner />
              </div>
            ) : overviewCadence === "one_off" ? (
              <OverviewOneOffTable
                data={overviewData}
                storeId={props.homeStore}
                onOpenTask={(row, tab) => openDrawerFor(row.plan_task?.uuid, row.date, row.intraday_slot_index, tab)}
              />
            ) : (
              <OverviewGrid
                data={overviewData}
                taskByUuid={taskByUuid}
                onCellPress={(planTask, cell) => cell.date && openDrawerFor(planTask.uuid, cell.date)}
              />
            )}
          </>
        ) : libraryError && !tabLoading ? (
          <TabLoadError onRetry={fetchPlanTasks} />
        ) : (
          <TaskLibrary
            planTasks={planTasks}
            roleNames={roleNames}
            storeGroups={storeGroups}
            canMutateTask={canMutateTask}
            onNewTask={openNewTask}
            onImportTemplates={
              taskTemplates.length > 0 ? () => setImportModalOpen(true) : null
            }
            onEditTask={openEditTask}
            onActivate={(t) => setConfirmActivate(t)}
            onPause={(t) => setTaskStatus(t, "paused", "Task paused. It stops appearing today.")}
            onResume={(t) => setTaskStatus(t, "active", "Task resumed")}
          />
        )}
      </div>

      <TaskDrawerHost
        ref={drawerHost}
        storeId={props.homeStore}
        storeName={(props.storeNames || []).find((s) => s.id === props.homeStore)?.store_name}
        homeCompany={props.homeCompany}
        canManage={canManage}
        permissionRole={props.permissionRole}
        tz={nowData?.timezone || overviewData?.timezone}
        todayStr={todayStr}
        roleNames={roleNames}
        planTasks={planTasks}
        onChanged={refreshLists}
      />

      <TaskTemplateImportModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        taskTemplates={taskTemplates}
        templateGroups={templateGroups}
        import_plan_task_templates={import_plan_task_templates}
        homeCompany={props.homeCompany}
        homeStore={props.homeStore}
        storeNames={props.storeNames}
        storeOptions={props.storeOptions}
        permissionRole={props.permissionRole}
      />

      <ConfirmDialog
        open={!!confirmActivate}
        tone="primary"
        title="Activate this task?"
        message={activationMessage(confirmActivate)}


        confirmLabel="Activate"
        onConfirm={() =>
          setTaskStatus(confirmActivate, "active", "Task activated. It starts appearing today.")
        }
        onCancel={() => setConfirmActivate(null)}
      >
        {confirmActivate && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
            <div className="text-sm font-semibold text-slate-900 break-words">
              {confirmActivate.number ? `#${confirmActivate.number} · ` : ""}
              {confirmActivate.name}
            </div>
            <div className="flex gap-1.5 flex-wrap mt-2">
              {[
                // A one-off's date chip names the DEADLINE, since the message
                // above names the day it appears (they can differ).
                ...(confirmActivate.cadence === "one_off"
                  ? [
                      "One-off",
                      confirmActivate.due_at
                        ? `Due ${formatDateTimeShort(
                            new Date(confirmActivate.due_at),
                            new Date(),
                            nowData?.timezone || overviewData?.timezone
                          )}`
                        : null,
                    ]
                  : [cadenceLabel(confirmActivate)]),
                windowLabel(confirmActivate),

                audienceLabel(confirmActivate, roleNames),
                scopeLabel(confirmActivate, storeGroups?.groups),
                confirmActivate.scoring_enabled ? "Scored audit" : null,
              ]
                .filter(Boolean)
                .map((label) => (
                  <span
                    key={label}
                    title={label}
                    className="inline-flex items-center text-xs font-semibold text-slate-600 bg-white border border-slate-200 rounded-full px-2.5 py-0.5 max-w-full truncate"
                  >
                    {label}
                  </span>

                ))}
            </div>
          </div>
        )}
      </ConfirmDialog>


    </div>
  );
}

// What activating this task means, in one sentence that follows the
// schedule the backend applies: a routine surfaces from today; a one-off
// surfaces on its own business date at each location (stated as the
// absolute date, since "today" differs across a company's timezones), and
// an untouched one-off drops off the Now list a week past its deadline.
function activationMessage(task) {
  if (!task) return "";
  if (task.cadence !== "one_off") {
    return "It starts appearing for everyone eligible from today.";
  }
  const onDate = task.one_off_date || null;
  const when = onDate
    ? `on ${new Date(`${onDate}T12:00:00`).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}`
    : "on its date";
  return `It appears at its locations ${when} (each location's own business day) and stays open until it is done, or until a week after its deadline if nobody starts it.`;
}

function NavBtn({ onClick, label, children }) {

  return (
    <button
      onClick={onClick}
      aria-label={label}
      className="w-8 h-8 shrink-0 inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:border-slate-400 shadow-sm"
    >
      {children}
    </button>
  );
}

export default DailyPlanDisplay;
